import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, User } from 'lucide-react';

import { useLogout } from '@/hooks/useLogout';
import { useUserStore } from '@/stores/user';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function Avatar({ initials, size = 'md' }: { initials: string; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-8 w-8 text-[11px]';
  return (
    <div
      className={`${dim} flex flex-shrink-0 items-center justify-center rounded-full font-bold text-white`}
      style={{ backgroundColor: '#0014cc' }}
    >
      {initials}
    </div>
  );
}

/**
 * User identity + menu rendered in the suite sidebar footer slot.
 *
 * Mirrors the legacy {@link UserProfile} but without the shadcn `useSidebar()`
 * context dependency, since the shared `SuiteShell` provides no such provider.
 */
export function SuiteUserMenu() {
  const navigate = useNavigate();
  const logout = useLogout();
  const profile = useUserStore((s) => s.profile);

  if (!profile) {
    return (
      <div className="flex flex-1 items-center gap-2.5">
        <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
          <div className="h-2.5 w-32 animate-pulse rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left outline-none transition-colors hover:bg-slate-50"
        >
          <Avatar initials={profile.initials} />
          <div className="grid min-w-0 flex-1 leading-tight">
            <span className="truncate text-[13px] font-semibold text-slate-800">
              {profile.display_name}
            </span>
            <span className="truncate text-[11px] text-slate-400">{profile.email}</span>
          </div>
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg bg-white shadow-lg"
        side="top"
        align="start"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar initials={profile.initials} size="sm" />
            <div className="grid min-w-0 flex-1 leading-tight">
              <span className="truncate text-sm font-semibold text-slate-800">
                {profile.display_name}
              </span>
              <span className="truncate text-xs text-slate-500">{profile.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate({ to: '/profile' })}>
            <User className="mr-2 h-5 w-5" />
            Profile
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          <LogOut className="mr-2 h-5 w-5" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
